import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from "formik";
import * as yup from "yup";

import { AuthContext } from "../../providers/AuthProvider";
import { PublicLayout } from "../../components/PublicLayout/index";
import { TextInput } from "../../components/inputs/TextInput/TextInput";
import { SelectInput } from "../../components/inputs/SelectInput/SelectInput";
import { MultiSelectInput } from "../../components/inputs/MultiSelectInput/MultiSelectInput";
import { InputAutoComplete } from "../../components/InputAutoComplete/InputAutoComplete";
import PhoneInput from "../../components/inputs/PhoneInput/PhoneInput";
import ButtonPrimary from "../../components/Buttons/ButtonPrimary/ButtonPrimary";
import logo from "../../assets/ecomlovers-logo-transparente.svg";
import logoAstro from "../../assets/image 11.png";
import iconHeart from "../../assets/Vector (23).png";
import astronautCongratulate from "../../assets/poses/astronout-standing-congratulate.svg";
import api from "../../utils/api";
import { getCombinedUtmParams } from "../../utils/utmHelper";

import "./TalentSignupForm.css";

// Constants
const SENIORITY_OPTIONS = [
  { value: 'assistant', label: 'Auxiliar' },
  { value: 'associate', label: 'Assistente' },
  { value: 'junior_analyst', label: 'Analista Jr.' },
  { value: 'analyst', label: 'Analista Pleno' },
  { value: 'senior_analyst', label: 'Analista Sênior' },
  { value: 'coordinator', label: 'Coordenador' },
  { value: 'manager', label: 'Gerente' },
  { value: 'director', label: 'Diretor' },
  { value: 'c_level', label: 'C-Level' }
];

const CONTRACT_TYPE_OPTIONS = [
  { value: 'clt', label: 'CLT' },
  { value: 'pj', label: 'PJ' }
];

const WORK_MODEL_OPTIONS = [
  { value: 'in_person', label: 'Presencial' },
  { value: 'remote', label: 'Home Office' },
  { value: 'hybrid', label: 'Híbrido' }
];

const SALARY_NEGOTIABLE_OPTIONS = [
  { value: "true", label: 'Sim' },
  { value: "false", label: 'Não' }
];

const TalentSignupForm = () => {
  const navigate = useNavigate();
  const { signUp } = useContext(AuthContext);

  const [utmParams, setUtmParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [brazilStates, setBrazilStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const params = getCombinedUtmParams();
    setUtmParams(params);
  }, []);

  // Fetch states from IBGE
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        );
        const sortedStates = data.sort((a, b) => a.nome.localeCompare(b.nome));
        setBrazilStates(sortedStates);
      } catch (error) {
        console.error("Erro ao carregar estados:", error);
      }
    };
    fetchStates();
  }, []);

  // Function to format currency
  const formatCurrency = (value) => {
    const numericValue = value.replace(/\D/g, '');

    if (!numericValue) return '';

    const numberValue = parseInt(numericValue, 10) / 100;

    return numberValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Format phone to API standard
  const formatPhoneForApi = (phone) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return `+55 (${phoneDigits.slice(0, 2)}) ${phoneDigits.slice(2, 3)} ${phoneDigits.slice(3, 7)}-${phoneDigits.slice(7, 11)}`;
  };

  // Process API errors
  const handleApiErrors = (error) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!error.response) {
      const msg = 'Erro de conexão. Verifique sua internet e tente novamente.';
      setMessage(msg);
      setMessageType('error');
      toast.error(msg);
      return;
    }

    const { status, data } = error.response;
    let errorMessages = [];

    if (status === 500) {
      errorMessages.push('Erro interno do servidor. Por favor, tente novamente mais tarde.');
    } else if (data && Array.isArray(data.errors) && data.errors.length > 0) {
      data.errors.forEach(err => {
        if (err.id === 'email' && err.title === 'Email já está em uso') {
          errorMessages.push('Este e-mail já está cadastrado em nossa plataforma.');
        } else if (err.id === 'phone' && err.title === 'Phone já está em uso') {
          errorMessages.push('Este telefone já está cadastrado em nossa plataforma.');
        } else {
          errorMessages.push(err.title || err.detail || 'Erro de validação');
        }
      });
    } else if (data && data.error) {
      errorMessages = [data.error];
    } else if (data && data.message) {
      errorMessages = [data.message];
    }

    const errorMessage = errorMessages.length > 0
      ? errorMessages.join('\n')
      : status === 401
        ? 'Erro de autenticação.'
        : status === 409
          ? 'Dados já cadastrados.'
          : status === 422
            ? 'Dados inválidos.'
            : 'Erro interno do servidor, tente novamente mais tarde.';

    setMessage(errorMessage);
    setMessageType('error');
    toast.error(
      <div style={{ whiteSpace: 'pre-line' }}>
        {errorMessage}
      </div>
    );
  };

  const form = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      birthdate: '',
      seniorityLevel: '',
      contractType: '',
      workModel: [],
      salary: '',
      salaryNegotiable: '',
      state: '',
      city: ''
    },
    validationSchema: yup.object({
      name: yup.string()
        .required('Nome é obrigatório')
        .test('full-name', 'Digite seu nome completo (nome e sobrenome)', (value) => {
          if (!value) return false;
          const words = value.trim().split(/\s+/);
          return words.length >= 2 && words.every(word => word.length > 0);
        }),
      email: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
      phone: yup.string().required('Telefone é obrigatório').min(11, 'Telefone deve ter pelo menos 11 dígitos'),
      birthdate: yup.string().required('Data de nascimento é obrigatória'),
      state: yup.string().required('Estado é obrigatório'),
      city: yup.string().required('Cidade é obrigatória'),
      seniorityLevel: yup.string().required('Nível de senioridade é obrigatório'),
      contractType: yup.string().required('Regime de contratação é obrigatório'),
      workModel: yup.array().min(1, 'Selecione pelo menos uma opção').required('Modelo de trabalho é obrigatório'),
      salary: yup.string().required('Pretensão salarial é obrigatória'),
      salaryNegotiable: yup.boolean().required('Escolha se está aberto à negociação salarial')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setMessage('');

      try {
        const talentData = {
          data: {
            type: 'talents',
            attributes: {
              name: values.name.trim(),
              email: values.email.trim().toLowerCase(),
              phone: formatPhoneForApi(values.phone),
              birthdate: values.birthdate,
              seniorityLevel: values.seniorityLevel,
              contractType: values.contractType,
              workModel: values.workModel,
              salary: values.salary ? parseFloat(values.salary.replace(/\D/g, '')) / 100 : 0,
              salaryNegotiable: values.salaryNegotiable,
              state: values.state,
              city: values.city,
              ...utmParams
            }
          }
        };

        const { data } = await api.post('/talents/talents', talentData);

        if (data?.data) {
          // GTM Event
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'formSubmissionSuccess',
            email: values.email,
            type: 'talent'
          });

          const result = await signUp(data.data);

          if (result === 'success') {
            const { authUrl: responseAuthUrl } = data.data.attributes;
            setAuthUrl(responseAuthUrl || '');
            toast.success('Conta criada com sucesso!');
          } else {
            setMessage('Erro ao processar cadastro. Tente novamente.');
            setMessageType('error');
          }
        }
      } catch (error) {
        console.error('Erro ao cadastrar talento:', error);
        handleApiErrors(error);
      } finally {
        setLoading(false);
      }
    }
  });

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (form.values.state) {
        try {
          const { data } = await axios.get(
            `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.values.state}/municipios`
          );
          const sortedCities = data.sort((a, b) => a.nome.localeCompare(b.nome));
          setCities(sortedCities);
        } catch (error) {
          console.error("Erro ao carregar cidades:", error);
        }
      } else {
        setCities([]);
      }
    };
    fetchCities();
  }, [form.values.state]);

  // Render form fields
  const renderFormFields = () => (
    <>
      {message && (
        <div className={`message-box ${messageType}`}>
          {message.includes('\n') ? (
            <ul style={{ paddingLeft: '20px', margin: 0, listStyleType: 'disc' }}>
              {message.split('\n').map((msg, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>{msg}</li>
              ))}
            </ul>
          ) : (
            message
          )}
        </div>
      )}

      <TextInput
        label="Nome Completo *"
        name="name"
        value={form.values.name}
        onChange={(value) => form.setFieldValue('name', value)}
        placeholder="Digite seu nome completo"
        error={form.touched.name && form.errors.name}
      />

      <TextInput
        label="E-mail *"
        name="email"
        type="email"
        value={form.values.email}
        onChange={(value) => form.setFieldValue('email', value)}
        placeholder="seu@email.com"
        error={form.touched.email && form.errors.email}
      />

      <PhoneInput
        label="Telefone *"
        name="phone"
        value={form.values.phone}
        onChange={(value) => form.setFieldValue('phone', value)}
        placeholder="(XX) XXXXX-XXXX"
        error={form.touched.phone && form.errors.phone}
      />

      <TextInput
        label="Data de Nascimento *"
        name="birthdate"
        type="date"
        value={form.values.birthdate}
        onChange={(value) => form.setFieldValue('birthdate', value)}
        error={form.touched.birthdate && form.errors.birthdate}
      />

      <InputAutoComplete
        label="Estado *"
        placeholder="Selecione seu estado"
        options={brazilStates}
        value={brazilStates.find((state) => state.sigla === form.values.state) || null}
        getOptionLabel={(option) => option.nome || ""}
        onChange={(event, newValue) => {
          const selectedValue = newValue?.sigla?.trim() || "";
          form.setFieldValue('state', selectedValue);
          form.setFieldValue('city', "");
        }}
        error={form.touched.state && form.errors.state}
        preventSubmitOnEnter={true}
      />

      <InputAutoComplete
        label="Cidade *"
        placeholder="Selecione sua cidade"
        options={cities}
        value={cities.find((city) => city.nome === form.values.city) || null}
        getOptionLabel={(option) => option.nome || ""}
        onChange={(event, newValue) => {
          const selectedValue = newValue?.nome?.trim() || "";
          form.setFieldValue('city', selectedValue);
        }}
        error={form.touched.city && form.errors.city}
        preventSubmitOnEnter={true}
      />

      <SelectInput
        label="Nível de Senioridade *"
        name="seniorityLevel"
        value={form.values.seniorityLevel}
        onChange={form.handleChange}
        options={SENIORITY_OPTIONS}
        placeholder="Selecione seu nível"
        error={form.touched.seniorityLevel && form.errors.seniorityLevel}
      />

      <SelectInput
        label="Regime de Contratação *"
        name="contractType"
        value={form.values.contractType}
        onChange={form.handleChange}
        options={CONTRACT_TYPE_OPTIONS}
        placeholder="Selecione o regime"
        error={form.touched.contractType && form.errors.contractType}
      />

      <MultiSelectInput
        label="Modelo de Trabalho *"
        name="workModel"
        value={form.values.workModel}
        onChange={(values) => form.setFieldValue('workModel', values)}
        options={WORK_MODEL_OPTIONS}
        placeholder="Selecione o modelo"
        error={form.touched.workModel && form.errors.workModel}
      />

      <TextInput
        label="Pretensão Salarial *"
        name="salary"
        value={form.values.salary}
        onChange={(value) => form.setFieldValue('salary', formatCurrency(value))}
        placeholder="R$ 0,00"
        error={form.touched.salary && form.errors.salary}
      />

      <SelectInput
        label="Aberto à negociação salarial *"
        name="salaryNegotiable"
        options={SALARY_NEGOTIABLE_OPTIONS}
        value={
          form.values.salaryNegotiable === true
            ? "true"
            : form.values.salaryNegotiable === false
              ? "false"
              : ""
        }
        onChange={(e) => {
          const value = e.target.value;
          form.setFieldValue("salaryNegotiable", value === "true" ? true : value === "false" ? false : null);
        }}
        placeholder="Selecione uma opção"
        error={form.touched.salaryNegotiable && form.errors.salaryNegotiable}
      />
    </>
  );

  // Render form buttons
  const renderFormButtons = () => (
    <>
      <div className="contianerButtonsLogin">
        <ButtonPrimary
          text={loading ? 'Cadastrando...' : 'Criar Conta'}
          onClick={form.handleSubmit}
          disabled={loading}
          loading={loading}
          fontSize="14px"
          fontWeight={700}
        />
      </div>

      <div style={{ textAlign: "center", color: "#211E1F", fontSize: "14px" }}>
        Já tem uma conta?{" "}
        <span
          onClick={() => navigate("/login")}
          style={{ color: "#EC5569", cursor: "pointer", fontWeight: "600" }}
        >
          Fazer login
        </span>
      </div>
    </>
  );

  return (
    <>
      <PublicLayout>
        <div className="boxSignupFormContainer">
          <div className="title">
            <h2>
              Cadastro
            </h2>

            <p>
              Preencha seus dados para se cadastrar
            </p>
          </div>

          <form onSubmit={form.handleSubmit} className="signupForm">
            {renderFormFields()}
          </form>

          {renderFormButtons()}
        </div>
      </PublicLayout>
      <ToastContainer />
    </>
  )
};

export default TalentSignupForm;
